import React, { useCallback, useEffect, useMemo, useState } from "react";

import cloneDeep from "lodash/cloneDeep";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Grid from "@cloudscape-design/components/grid";
import Badge from "@cloudscape-design/components/badge";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";
import Spinner from "@cloudscape-design/components/spinner";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import Tabs from "@cloudscape-design/components/tabs";
import DatePicker from "@cloudscape-design/components/date-picker";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { Container } from "react-bootstrap";

/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { playerTagStore } from "../stores/playerTag";
import { configStore } from "../stores/config";
import { playerRulesAddModalStore } from "../stores/playerRulesAddModal";
import { playerRulesEditModalStore } from "../stores/playerRulesEditModal";
import { userStore } from "../stores/access";

/* ------- */

function PlayerTag(props) {
	const [activeTabId, setActiveTabId] = useState("rules");


	const [statisticsPeriod, setStatisticsPeriod] = useState("day");
	const [statisticsStartDate, setStatisticsStartDate] = useState("");
	const [statisticsEndDate, setStatisticsEndDate] = useState("");
	const [statisticsLoading, setStatisticsLoading] = useState(false);
	const [statisticsExportLoading, setStatisticsExportLoading] = useState(false);
	const [statisticsError, setStatisticsError] = useState("");
	const [statisticsData, setStatisticsData] = useState([]);
	const [statisticsTotal, setStatisticsTotal] = useState(null);
	const [statisticsTotalsByProject, setStatisticsTotalsByProject] = useState({});
	const [statisticsTotalsByActionRule, setStatisticsTotalsByActionRule] = useState({});
	const [statisticsActionRuleStats, setStatisticsActionRuleStats] = useState({});
	const [selectedProject, setSelectedProject] = useState({
		value: "all",
		label: "All projects"
	});
	const [selectedStatisticsFilters, setSelectedStatisticsFilters] = useState([]);
	const [selectedStatisticsActionRules, setSelectedStatisticsActionRules] = useState([]);
	const [statisticsRefererId, setStatisticsRefererId] = useState("");

	const statisticsFilterOptions = [
		{ value: "new_player_none_dep", label: "new_player_none_dep" },
		{ value: "new_player_wtih_dep", label: "new_player_wtih_dep" },
		{ value: "all_player_with_none_dep", label: "all_player_with_none_dep" },
		{ value: "all_player_with_one_dep", label: "all_player_with_one_dep" },
		{ value: "all_player_with_more_one_dep", label: "all_player_with_more_one_dep" },
		{ value: "old_player_with_none_dep", label: "old_player_with_none_dep" },
		{ value: "old_player_with_one_dep", label: "old_player_with_one_dep" },
		{ value: "old_player_with_more_one_dep", label: "old_player_with_more_one_dep" },
		{ value: "by_referer", label: "by_referer" },
		{ value: "none", label: "none" },
	];

	const statisticsActionRuleOptions = [
		{ value: "forbidden_deposit", label: "forbidden_deposit" },
		{ value: "forbidden_withdrawal", label: "forbidden_withdrawal" },
		{ value: "data_users_bans", label: "data_users_bans" },
		{ value: "user_zero_balance", label: "user_zero_balance" },
	];



	const handleTabChange = (event) => setActiveTabId(event.detail.activeTabId);

	const handleKeyPress = (event) => {
		if (event.detail.key === "Enter") {
			setTimeout(() => {
				props.Interface.loadPlayerTagList();
			}, 0);
		}
	};

	const handleInputDebounceChange = (input) => {
		props.setPlayerTag({
			...props.playerTag,
			currentSearch: input.detail.value,
			pagination: {
				...props.playerTag.pagination,
				current: 1,
			},
		});

		if (input.detail.value.length === 0) {
			setTimeout(() => props.Interface.loadPlayerTagList(), 250);
		}
	};

	useEffect(() => {
		props.Interface.loadPlayerTagList();
	}, []);

	const handleDateChange = (which) => (e) => {
		const v = e.detail.value || "";
		if (which === "statisticsStartDate") setStatisticsStartDate(v);
		else setStatisticsEndDate(v);
	};

	const handleStatisticsFiltersChange = useCallback(({ detail }) => {
		const nextOptions = detail.selectedOptions || [];
		const hasNone = nextOptions.some((option) => option.value === "none");

		if (!hasNone || nextOptions.length === 1) {
			setSelectedStatisticsFilters(nextOptions);
			return;
		}

		const previouslySelectedNone = selectedStatisticsFilters.some((option) => option.value === "none");

		setSelectedStatisticsFilters(
			previouslySelectedNone
				? nextOptions.filter((option) => option.value !== "none")
				: nextOptions.filter((option) => option.value === "none")
		);
	}, [selectedStatisticsFilters]);

	const statisticsFilterValues = useMemo(() => {
		const values = selectedStatisticsFilters
			.map((option) => option.value)
			.filter(Boolean);

		if (!values.length || values.includes("none")) return [];

		return values;
	}, [selectedStatisticsFilters]);

	const isByRefererSelected = statisticsFilterValues.includes("by_referer");

	const statisticsGlobalActionRuleTotals = useMemo(() => {
		return Object.entries(statisticsTotalsByActionRule || {})
			.map(([actionRule, totalsByProject]) => ({
				actionRule,
				total: Object.values(totalsByProject || {}).reduce(
					(sum, value) => sum + (Number(value) || 0),
					0
				),
			}))
			.sort((left, right) => left.actionRule.localeCompare(right.actionRule));
	}, [statisticsTotalsByActionRule]);

	const statisticsTotalByActionTag = useMemo(() => {
		return statisticsGlobalActionRuleTotals.reduce(
			(total, item) => total + (Number(item.total) || 0),
			0
		);
	}, [statisticsGlobalActionRuleTotals]);

	const statisticsRequestPayload = useMemo(() => {
		const actionRule = selectedStatisticsActionRules.length
			? selectedStatisticsActionRules.map((option) => option.value)
			: undefined;
		const refererId =
			isByRefererSelected && statisticsRefererId.trim()
				? statisticsRefererId.trim()
				: undefined;

		return {
			period: statisticsPeriod,
			startDate: statisticsStartDate,
			endDate: statisticsEndDate,
			projects: selectedProject.value,
			filters: statisticsFilterValues,
			actionRule,
			refererId,
		};
	}, [
		isByRefererSelected,
		selectedProject,
		selectedStatisticsActionRules,
		statisticsEndDate,
		statisticsFilterValues,
		statisticsPeriod,
		statisticsRefererId,
		statisticsStartDate,
	]);

	const isStatisticsDateRangeSelected = Boolean(statisticsStartDate && statisticsEndDate);

	const loadStatistics = useCallback(async () => {
		try {
			setStatisticsError("");
			setStatisticsLoading(true);
			const res = await props.Interface.loadPlayerTagStats(statisticsRequestPayload);

			if (!res || res.success !== true) {
				throw new Error(res?.error || "Failed to load statistics");
			}

			setStatisticsData(Array.isArray(res.data) ? res.data : []);
			setStatisticsTotal(typeof res.total !== "undefined" ? res.total : null);
			setStatisticsTotalsByProject(res.totalsByProject || {});
			setStatisticsTotalsByActionRule(res.totalsByActionRule || {});
			setStatisticsActionRuleStats(res.actionRuleStats || {});
		} catch (err) {
			setStatisticsError(err.message || "Unknown error");
			setStatisticsData([]);
			setStatisticsTotal(null);
			setStatisticsTotalsByProject({});
			setStatisticsTotalsByActionRule({});
			setStatisticsActionRuleStats({});
		} finally {
			setStatisticsLoading(false);
		}
	}, [props.Interface, statisticsRequestPayload]);

	const handleStatisticsExportXlsx = useCallback(async () => {
		if (!isStatisticsDateRangeSelected || statisticsExportLoading) return;

		try {
			setStatisticsError("");
			setStatisticsExportLoading(true);

			const response = await props.Interface.request(
				"get",
				{ class: "panel", module: "playerTag", method: "statisticsExportXlsx" },
				statisticsRequestPayload
			);

			const data = response.data;

			const filename = data.filename;
			const mimeType = data.mimeType;
			const base64File = data.fileBase64;


			if (!base64File) {
				throw new Error("Invalid XLSX export response");
			}

			const cleanedBase64 = String(base64File).includes(",")
				? String(base64File).split(",").pop()
				: String(base64File);
			const binary = window.atob(cleanedBase64);
			const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
			const blob = new Blob([bytes], { type: mimeType });
			const link = document.createElement("a");

			link.href = URL.createObjectURL(blob);
			link.download = filename;
			link.click();
			URL.revokeObjectURL(link.href);
		} catch (err) {
			setStatisticsError(err?.text || err?.message || "Failed to export XLSX");
		} finally {
			setStatisticsExportLoading(false);
		}
	}, [isStatisticsDateRangeSelected, statisticsExportLoading, props.Interface, statisticsRequestPayload]);

	return (
		<>
			{props.playerTag.loaded && props.playerTag.error.text.length ? (
				<Box
					margin={{ bottom: "m" }}
					children={
						<Alert
							type="error"
							header="Error loading data:"
							children={
								<>
									<Box variant="p" children={props.playerTag.error.text} />
									<hr />
									<Box
										variant="p"
										children={
											<>
												request error code: {props.playerTag.error.code} | request timestamp:{" "}
												{props.playerTag.error.timestamp}
											</>
										}
									/>
								</>
							}
						/>
					}
				/>
			) : null}

			<Tabs
				activeTabId={activeTabId}
				onChange={handleTabChange}
				tabs={[
					{
						id: "rules",
						label: "Player tag",
						content: (
							<Table
								stickyHeader
								trackBy="id"
								loading={!props.playerTag.loaded}
								sortingColumn={{
									sortingField: props.playerTag.sorting.sortingColumn.column,
								}}
								sortingDescending={props.playerTag.sorting.isDescending}
								onSortingChange={(sorting) => {
									props.setPlayerTag({
										...props.playerTag,
										sorting: {
											...props.playerTag.sorting,
											sortingColumn: {
												order: !sorting.detail.isDescending ? "ASC" : "DESC",
												column: sorting.detail.sortingColumn.sortingField,
											},
											isDescending: sorting.detail.isDescending,
										},
									});

									setTimeout(() => {
										props.Interface.loadPlayerTagList();
									}, 250);
								}}
								columnDefinitions={[
									{
										id: "uid",
										header: "UID",
										sortingField: "uid",
										cell: (item) => (
											<Button
												iconName={
													typeof item.verify !== "undefined" && item.verify.status === 3
														? "status-stopped"
														: typeof item.verify !== "undefined" && item.verify.verified
															? "status-positive"
															: "status-in-progress"
												}
												className={props.styles?.["btnUID"]}
												variant="normal"
												onClick={() => {
													props.Interface.loadInfo(item.uid, item.project);
												}}
												children={item.uid}
											/>
										),
									},
									{
										id: "project",
										header: "Project",
										sortingField: "project",
										cell: (item) => (
											<img
												className={props.styles?.["withdrawalsBrand"]}
												src={
													document.body.classList[0] === "awsui-dark-mode"
														? props.user.brandsList[item.project - 1].logoDark
														: props.user.brandsList[item.project - 1].logo
												}
												alt="brand"
											/>
										),
									},
									{ id: "tag", header: "Tag", sortingField: "tag", cell: (item) => item.tag },
									{
										id: "date",
										header: "Date",
										sortingField: "date",
										cell: (item) => (
											<>
												{new Date(item.date).toLocaleDateString() +
													" " +
													new Date(item.date).toLocaleTimeString()}
											</>
										),
									},
								]}
								items={props.playerTag.data}
								pagination={
									<Pagination
										disabled={!props.playerTag.loaded}
										currentPageIndex={props.playerTag.pagination.current}
										pagesCount={props.playerTag.pagination.total}
										onChange={(page) => {
											props.setPlayerTag({
												...props.playerTag,
												pagination: {
													...props.playerTag.pagination,
													current: page.detail.currentPageIndex,
												},
											});
											setTimeout(() => {
												props.Interface.loadPlayerTagList();
											}, 250);
										}}
									/>
								}
								filter={
									<Input
										disabled={!props.playerTag.loaded}
										disableBrowserAutocorrect={true}
										autoComplete={false}
										placeholder="Tag"
										type="search"
										inputMode="search"
										onKeyDown={handleKeyPress}
										value={props.playerTag.currentSearch}
										onChange={handleInputDebounceChange}
									/>
								}
								preferences={
									<Preferences
										cancelLabel="Cancel"
										confirmLabel="Confirm"
										pageSizePreference={{
											title: "Player tags per page",
											options: props.config.playerTag.preferences.perPage,
										}}
										preferences={{ pageSize: props.config.playerTag.perPage }}
										title="Preferences"
										onConfirm={(preferences) => {
											localStorage.setItem("playerTag.perPage", preferences.detail.pageSize);
											props.setConfig({
												...props.config,
												playerTag: {
													...props.config.playerTag,
													perPage: preferences.detail.pageSize,
												},
											});

											setTimeout(() => {
												props.Interface.loadPlayerTagList();
											}, 250);
										}}
									/>
								}
								header={
									<Box
										margin={{ bottom: "m" }}
										children={
											<Header
												children="Player tags"
												counter={<>( {props.playerTag.pagination.count} )</>}
											/>
										}
									/>
								}
								empty="No playerTag found with requested filter"
							/>
						),
					},
					{
						id: "statistics",
						label: "Statistics",
						content: (
							<Box>
								<Container
									header={
										<Header
											variant="h2"
											description="View statistics of player tags over different time periods"
										>
											Player tags statistics
										</Header>
									}
								>
									<Box margin={{ bottom: "m" }}>
										<SpaceBetween size="s" direction="horizontal">
										
												<Select
													selectedOption={{
														value: statisticsPeriod || "day",
														label:
															statisticsPeriod === "day"
																? "Day"
																: statisticsPeriod === "week"
																	? "Week"
																	: "Month",
													}}
													onChange={({ detail }) => setStatisticsPeriod(detail.selectedOption.value)}
													options={[
														{ value: "day", label: "Day" },
														{ value: "week", label: "Week" },
														{ value: "month", label: "Month" },
													]}
													placeholder="Select Period"
												/>

  												<DatePicker
													value={statisticsStartDate}
													onChange={handleDateChange("statisticsStartDate")}
													placeholder="Start Date"
												/>

												<DatePicker
													value={statisticsEndDate}
													onChange={handleDateChange("statisticsEndDate")}
													placeholder="End Date"
												/>

												<Select
													selectedOption={selectedProject}
													onChange={({ detail }) => setSelectedProject(detail.selectedOption)}
													options={[
														{ value: "all", label: "All projects" },
														...props.user.brandsList.map((b, idx) => ({
															value: b.id ?? (idx + 1),
															label: b.name
														}))
													]}
													placeholder="Select Project"
												/>
												<Multiselect
													selectedOptions={selectedStatisticsFilters}
													onChange={handleStatisticsFiltersChange}
													options={statisticsFilterOptions}
													placeholder="Select filters"
												/>
												{isByRefererSelected && (
													<Input
														value={statisticsRefererId}
														onChange={({ detail }) => setStatisticsRefererId(detail.value)}
														placeholder="Enter referer ID"
													/>
												)}
												<Multiselect
													selectedOptions={selectedStatisticsActionRules}
													onChange={({ detail }) => setSelectedStatisticsActionRules(detail.selectedOptions)}
													options={statisticsActionRuleOptions}
													placeholder="Select automatic filters"
												/>

												

												
						
											<Button onClick={loadStatistics}>Submit</Button>
											<Button
												variant="normal"
												onClick={handleStatisticsExportXlsx}
												disabled={!isStatisticsDateRangeSelected || statisticsExportLoading}
												loading={statisticsExportLoading}
											>
												Export XLSX
											</Button>
										
										</SpaceBetween>
									</Box>

									{statisticsError && (
										<Box margin={{ top: "s" }}>
											<Alert type="error" header="Error" children={statisticsError} />
										</Box>
									)}

									{statisticsLoading ? (
										<Box textAlign="center">
											<Spinner size="large" />
										</Box>
									) : (
										<Box>
											{(statisticsTotal !== null || statisticsGlobalActionRuleTotals.length > 0) && (
												<Box margin={{ bottom: "l" }}>
													<SpaceBetween size="m">
														{statisticsTotal !== null && (
															<SpaceBetween size="xs">
																<Box fontSize="heading-m" fontWeight="bold">
																	All total players: {statisticsTotal}
																</Box>
																<Box fontSize="heading-m" fontWeight="bold">
																	All total by action tag: {statisticsTotalByActionTag}
																</Box>
															</SpaceBetween>
														)}
														{statisticsGlobalActionRuleTotals.length > 0 && (
															<SpaceBetween size="xs">
																{statisticsGlobalActionRuleTotals.map((item) => (
																	<Box key={item.actionRule} fontSize="body-s" fontWeight="bold">
																		All total by action rule {item.actionRule}: {item.total}
																	</Box>
																))}
															</SpaceBetween>
														)}
													</SpaceBetween>
												</Box>
											)}
											{statisticsData.length > 0 ? (
												Array.from(new Set(statisticsData.map(d => d.project))).sort((a, b) => Number(a) - Number(b)).map(pid => {
													const brand = props.user.brandsList.find(
														b => b.projectId === pid || b.id === pid
													);
													const brandName = brand?.name || pid;
													const brandTotal =
														statisticsTotalsByProject?.[brand?.id] ??
														statisticsTotalsByProject?.[String(brand?.id)] ??
														statisticsTotalsByProject?.[pid] ??
														statisticsTotalsByProject?.[String(pid)] ??
														0;

													const projectRows = statisticsData.filter((row) => row.project === pid);
													const allDates = Array.from(
														new Set(
															projectRows.flatMap((row) => Object.keys(row.byDateLabel || {}))
														)
													);
													const tableItems = projectRows.map((row) => ({
														tag: row.tag,
														actionType: row.actionType,
														total: row.totalByProject ?? 0,
														...Object.fromEntries(
															allDates.map((date) => [date, row.byDateLabel?.[date]?.uniqueUsers ?? 0])
														),
													}));

													const columns = [
														{
															id: "tag",
															header: "Tag",
															cell: item => item.actionType ?  `${item.tag} (${item.actionType})` : item.tag,
															minWidth: 800,
															maxWidth: 800,
															width: 800
														},
														{
															id: "total",
															header: "Total",
															cell: item => item.total,
															minWidth: 80,
															maxWidth: 140,
														},
														...allDates.map(date => ({
															id: date,
															header: date,
															cell: item => item[date],
															minWidth: 80,
															maxWidth: 300
														}))
													];

													const projectKey = brand?.id ?? pid;
													const selectedActionRuleSet = new Set(selectedStatisticsActionRules.map((option) => option.value));
													const projectActionRuleItems = Object.entries(statisticsActionRuleStats || {})
														.filter(([actionRuleName]) => selectedActionRuleSet.has(actionRuleName))
														.map(([actionRuleName, actionRuleStat]) => {
															const total =
																actionRuleStat?.totalsByProject?.[projectKey] ??
																actionRuleStat?.totalsByProject?.[String(projectKey)] ??
																actionRuleStat?.totalsByProject?.[pid] ??
																actionRuleStat?.totalsByProject?.[String(pid)] ??
																0;
															const byDateLabel = actionRuleStat?.byDateLabel || {};
															const projectDateValues = Object.fromEntries(
																Object.entries(byDateLabel).map(([dateLabel, dateStat]) => [
																	dateLabel,
																	dateStat?.totalsByProject?.[projectKey] ??
																		dateStat?.totalsByProject?.[String(projectKey)] ??
																		dateStat?.totalsByProject?.[pid] ??
																		dateStat?.totalsByProject?.[String(pid)] ??
																		0,
																])
															);

															return {
																actionRule: actionRuleName,
																total,
																...projectDateValues,
															};
														})
														.filter((item) =>
															item.total > 0 ||
															Object.entries(item).some(([key, value]) => key !== "actionRule" && key !== "total" && Number(value) > 0)
														);
													const projectActionRuleDates = Array.from(
														new Set(
															projectActionRuleItems.flatMap((item) =>
																Object.keys(item).filter((key) => key !== "actionRule" && key !== "total")
															)
														)
													);
													const projectActionRuleColumns = [
														{
															id: "actionRule",
															header: "Action Rule",
															cell: (item) => item.actionRule,
															minWidth: 280,
															maxWidth: 420,
														},
														{
															id: "total",
															header: "Total",
															cell: (item) => item.total,
															minWidth: 80,
															maxWidth: 140,
														},
														...projectActionRuleDates.map((date) => ({
															id: date,
															header: date,
															cell: (item) => item[date] || 0,
															minWidth: 80,
															maxWidth: 300,
														})),
													];


													return (
														<Box key={pid} margin={{ bottom: "l" }}>
															{selectedStatisticsActionRules.length > 0 && projectActionRuleItems.length > 0 && (
																<Box margin={{ bottom: "m" }}>
																	<Table
																		columnDefinitions={projectActionRuleColumns}
																		items={projectActionRuleItems}
																		header={<Header>{`${brandName} action rules`}</Header>}
																		empty="No action rule data for this project"
																	/>
																</Box>
															)}
															<Table
																columnDefinitions={columns}
																items={tableItems}
																header={<Header>{`${brandName} (${brandTotal})`}</Header>}
																empty="No data for this project"
															/>
														</Box>
													);
												})
											) : (
												<Box>{statisticsTotal !== null ? "No statistics data found." : "Please select date."}</Box>
											)}
										</Box>
									)}
								</Container>
							</Box>
						)
					}



				]}
			/>
		</>
	);
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const playerTag = useRecoilValue(playerTagStore);
		const setPlayerTag = useSetRecoilState(playerTagStore);

		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);

		const playerRulesAddModal = useRecoilValue(playerRulesAddModalStore);
		const setPlayerRulesAddModal = useSetRecoilState(playerRulesAddModalStore);

		const playerRulesEditModal = useRecoilValue(playerRulesEditModalStore);
		const setPlayerRulesEditModal = useSetRecoilState(playerRulesEditModalStore);

		const user = useRecoilValue(userStore);

		return (
			<Component
				{...props}
				user={user}
				playerRulesEditModal={playerRulesEditModal}
				setPlayerRulesEditModal={setPlayerRulesEditModal}
				playerRulesAddModal={playerRulesAddModal}
				setPlayerRulesAddModal={setPlayerRulesAddModal}
				config={config}
				setConfig={setConfig}
				playerTag={playerTag}
				setPlayerTag={setPlayerTag}
			/>
		);
	};
}

export default withRecoil(PlayerTag);
